import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Button, InlineNotification, TextBox, useTheme } from '@veneer/core';

import { useDependencyManagerContext } from '../../../contexts/dependencyManager';
import { Device } from '../../../types/devices';
import { OperationStatus } from '../../../types';
import { RootState } from '../../../store';
import {
  postDeviceSharingRequest,
  postDeviceSharingCleanup
} from '../../../features/shared-access/shared-access-slice';
import { isEmail } from '../../../utils/common';
import { ModalContent, NotificationList, StyledShareModal } from '../styled';

export type ShareDeviceModalProps = {
  show: boolean;
  onClose: () => void;
  device: Device;
  'data-testid'?: string;
};

const ShareDeviceModal: React.FC<ShareDeviceModalProps> = (props) => {
  const { show, onClose, 'data-testid': dataTestId, device } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const { translate } = useDependencyManagerContext();

  const unknownDeviceName = translate('components.device-item.no-data.unknown');
  const deviceFamily = device.device_family || unknownDeviceName;
  const serialNumber = device.device_serial_nr;

  const { status: sharingStatus, error: sharingError } = useSelector(
    (state: RootState) => state.SharedAccessReducer.postDeviceSharingRequest
  );
  const { user } = useSelector((state: RootState) => state.UserReducer);
  const userEmail = user?.email?.value;

  const isLoading = sharingStatus === OperationStatus.RUNNING;
  const isSuccess = sharingStatus === OperationStatus.COMPLETED;
  const isAlreadyShared =
    sharingStatus === OperationStatus.ERRORED &&
    sharingError?.statusCode === 200;
  const isError = sharingStatus === OperationStatus.ERRORED && !isAlreadyShared;

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const hasStartedTyping = useRef(false);

  const grantAccessButtonAnalyticsAttributes = useMemo(
    () => ({
      'data-gtm-category': 'linkClick',
      'data-gtm-id': `share-device-modal-grant-access-button-${serialNumber}`,
      'data-gtm-value': `share-device-modal-grant-access-${deviceFamily}-${serialNumber}`
    }),
    [serialNumber, deviceFamily]
  );

  useEffect(() => {
    if (isAlreadyShared) {
      setEmailError(true);
      setErrorMessage(
        translate('components.share-device-modal.input.error-already-shared')
      );
    }
  }, [isAlreadyShared, translate]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (hasStartedTyping.current) {
        if (userEmail?.toLowerCase() === email.toLowerCase()) {
          setEmailError(true);
          setErrorMessage(
            translate('components.share-device-modal.input.error-own-email')
          );
        } else {
          setEmailError(!isEmail(email));
          setErrorMessage('');
        }
      }
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [userEmail, email, translate]);

  const handleClose = () => {
    setEmail('');
    setEmailError(false);
    hasStartedTyping.current = false;
    dispatch(postDeviceSharingCleanup());
    onClose();
  };

  const handleGrantAccess = () => {
    hasStartedTyping.current = true;
    if (!isEmail(email) || userEmail?.toLowerCase() === email.toLowerCase()) {
      setEmailError(true);
      return;
    }
    dispatch(postDeviceSharingRequest({ user: { email }, device }));
  };

  if (!show) return null;

  const modalTitle = translate(
    isSuccess
      ? 'components.share-device-modal.success.title'
      : 'components.share-device-modal.title'
  );

  const notificationType = isError ? 'negative' : 'informative';

  const notificationTitle = translate(
    isError
      ? 'components.share-device-modal.error-notification.title'
      : 'components.share-device-modal.notification.title'
  );

  const notificationDescription = isError ? (
    translate('components.share-device-modal.error-notification.description', {
      email
    })
  ) : (
    <NotificationList>
      <li>{translate('components.share-device-modal.notification.item-1')}</li>
      <li>{translate('components.share-device-modal.notification.item-2')}</li>
      <li>{translate('components.share-device-modal.notification.item-3')}</li>
    </NotificationList>
  );

  const notificationDataTestId = isError
    ? `${dataTestId}-error-notification`
    : `${dataTestId}-inline-notification`;

  const modalFooter = isSuccess ? (
    <Button
      appearance="secondary"
      onClick={handleClose}
      data-testid={`${dataTestId}-done-button`}
      aria-label={translate(
        'components.share-device-modal.success.done-aria-label',
        { deviceFamily, serialNumber }
      )}
    >
      {translate('components.share-device-modal.success.button')}
    </Button>
  ) : (
    <Button
      appearance="secondary"
      onClick={handleGrantAccess}
      loading={isLoading}
      data-testid={`${dataTestId}-grant-access-button`}
      aria-label={translate(
        'components.share-device-modal.button.grant-access-aria-label',
        { deviceFamily, serialNumber }
      )}
      {...grantAccessButtonAnalyticsAttributes}
    >
      {translate('components.share-device-modal.button.grant-access')}
    </Button>
  );

  const modalContent = isSuccess ? (
    <p data-testid={`${dataTestId}-success-description`}>
      {translate('components.share-device-modal.success.description', {
        email
      })}
    </p>
  ) : (
    <>
      <InlineNotification
        type={notificationType}
        title={notificationTitle}
        description={notificationDescription}
        closeButton={false}
        hideIcon={!isError}
        data-testid={notificationDataTestId}
      />
      <TextBox
        label={translate('components.share-device-modal.input.label')}
        placeholder={translate(
          'components.share-device-modal.input.placeholder'
        )}
        value={email}
        onChange={(value) => {
          value = value.trim();
          hasStartedTyping.current = true;
          if (isError || isAlreadyShared) {
            dispatch(postDeviceSharingCleanup());
          }
          setEmail(value);
        }}
        error={emailError}
        helperText={
          emailError
            ? errorMessage ||
              translate('components.share-device-modal.input.error')
            : undefined
        }
        maxLength={254}
        separateLabel
        data-testid={`${dataTestId}-email-input`}
      />
    </>
  );

  return (
    <StyledShareModal
      show={show}
      alignFooter="end"
      closeButton
      expanded
      onClose={handleClose}
      title={modalTitle}
      footer={modalFooter}
      data-testid={dataTestId}
    >
      <ModalContent $veneerTheme={theme}>{modalContent}</ModalContent>
    </StyledShareModal>
  );
};

export default ShareDeviceModal;
